import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getPriorAnalyses,
  getScan,
  manuallyEnqueueScanForAnalysis,
  updateScan,
  type AdminScanDetail,
  type LlmCallEntry,
  type PriorAnalysisSummary,
} from '../lib/api';
import { validateRegistrationPlate } from '@obd-reader/shared';
import type { ScanAnalysisReview, ScanAnalysisReviewStatus } from '@obd-reader/shared';
import {
  ProposedAppChangeResolutionControl,
  ResolutionBadge,
} from '../components/ProposedAppChangeResolutionControl';
import { theme } from '../theme';

export function ScanDetailPage() {
  const { scanId = '' } = useParams<{ scanId: string }>();
  const [scan, setScan] = useState<AdminScanDetail | null>(null);
  const [priorAnalyses, setPriorAnalyses] = useState<PriorAnalysisSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [analyseReason, setAnalyseReason] = useState('');
  const [analyseState, setAnalyseState] = useState<
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'sent'; enqueuedAt: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  useEffect(() => {
    setScan(null);
    setPriorAnalyses([]);
    setAnalyseState({ kind: 'idle' });
    setAnalyseReason('');
    getScan(scanId)
      .then(setScan)
      .catch((err: Error) => setError(err.message));
    // Prior analyses load independently — the main page renders the
    // moment the scan itself arrives; the prior-analyses section appears
    // when its fetch resolves. Best-effort: a failure here just leaves
    // the section unrendered (no error UI clutter).
    getPriorAnalyses(scanId)
      .then((res) => setPriorAnalyses(res.analyses))
      .catch(() => {});
  }, [scanId]);

  const sendForAnalysis = async () => {
    setAnalyseState({ kind: 'sending' });
    try {
      const res = await manuallyEnqueueScanForAnalysis(scanId, analyseReason);
      setAnalyseState({ kind: 'sent', enqueuedAt: res.enqueuedAt });
    } catch (err) {
      setAnalyseState({ kind: 'error', message: (err as Error).message });
    }
  };

  if (error) return <p style={{ color: theme.status.error }}>Error: {error}</p>;
  if (!scan) return <p>Loading…</p>;

  return (
    <div>
      <Link to="/scans">← back to scans</Link>
      <h1 style={{ marginTop: 12 }}>
        {scan.scanShortcodeBody ? `Scan OB-${scan.scanShortcodeBody}` : `Scan ${scan.scanId}`}
      </h1>
      {scan.scanShortcodeBody && (
        <p
          style={{
            marginTop: 0,
            fontSize: 12,
            color: theme.text.tertiary,
            fontFamily: 'monospace',
          }}
        >
          internal: {scan.scanId}
        </p>
      )}
      {scan.outcome === 'success' && scan.operatorOutcome !== 'failed' && (
        <ManualAnalyseControl
          reason={analyseReason}
          onReasonChange={setAnalyseReason}
          state={analyseState}
          onSend={() => void sendForAnalysis()}
        />
      )}
      <MarkAsFailedControl
        scan={scan}
        onUpdated={(patch: Partial<AdminScanDetail>) =>
          setScan((prev) => (prev ? { ...prev, ...patch } : prev))
        }
      />
      {scan.review && (
        <ReviewStatusSection
          scanId={scan.scanId}
          review={scan.review}
          onReviewUpdated={(review) => setScan((prev) => (prev ? { ...prev, review } : prev))}
        />
      )}
      {priorAnalyses.length > 0 && <PriorAnalysesSection analyses={priorAnalyses} />}
      {scan.llmUsage && <LlmCostsSection usage={scan.llmUsage} />}
      <dl style={styles.dl}>
        <Row label="Scanned at" value={formatTimestamp(scan.scannedAt)} />
        {typeof scan.scanDurationMs === 'number' && (
          <Row label="Scan duration" value={formatScanDuration(scan.scanDurationMs)} />
        )}
        <Row label="Outcome" value={scan.outcome} />
        <Row label="VIN" value={scan.vin ?? '—'} mono />
        <Row
          label="Registration plate"
          value={
            <RegistrationPlateEditor
              scanId={scan.scanId}
              current={scan.registrationPlate}
              onSaved={(plate) =>
                setScan((prev) => (prev ? { ...prev, registrationPlate: plate } : prev))
              }
            />
          }
        />
        {(scan.reportUrl || scan.reportUrlLatest) && (
          <Row
            label="Report PDF"
            value={
              <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {scan.reportUrlLatest && (
                  <a href={scan.reportUrlLatest} target="_blank" rel="noreferrer">
                    Open latest
                  </a>
                )}
                {scan.reportUrl && (
                  <a href={scan.reportUrl} target="_blank" rel="noreferrer">
                    Open this scan's snapshot
                  </a>
                )}
              </span>
            }
          />
        )}
        {(scan.batteryReportUrl || scan.batteryReportUrlLatest) && (
          <Row
            label="Battery Condition PDF"
            value={
              <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {scan.batteryReportUrlLatest && (
                  <a href={scan.batteryReportUrlLatest} target="_blank" rel="noreferrer">
                    Open latest
                  </a>
                )}
                {scan.batteryReportUrl && (
                  <a href={scan.batteryReportUrl} target="_blank" rel="noreferrer">
                    Open this scan's snapshot
                  </a>
                )}
              </span>
            }
          />
        )}
        <Row label="Captured from" value={formatCapturedFrom(scan.capturedFrom)} />
      </dl>

      {scan.summary && (
        <>
          <h2>Summary</h2>
          <CodeBlock data={scan.summary} />
        </>
      )}
      {scan.failureReport && (
        <>
          <h2>Failure report</h2>
          <CodeBlock data={scan.failureReport} />
        </>
      )}
      {scan.enriched && (
        <>
          <h2>Raw enriched payload</h2>
          <CodeBlock data={scan.enriched} />
        </>
      )}
    </div>
  );
}

type AnalyseState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; enqueuedAt: string }
  | { kind: 'error'; message: string };

function ManualAnalyseControl({
  reason,
  onReasonChange,
  state,
  onSend,
}: {
  reason: string;
  onReasonChange: (value: string) => void;
  state: AnalyseState;
  onSend: () => void;
}) {
  const disabled = state.kind === 'sending';
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>Send for AI analysis</strong>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          Bypasses the global trigger mode. Idempotent — duplicate clicks won't requeue.
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input
          type="text"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="Optional reason (e.g. 'customer complaint — re-check')"
          disabled={disabled}
          style={{
            flex: 1,
            padding: '6px 10px',
            background: theme.surface.header,
            color: theme.text.primary,
            border: `1px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.md,
            fontSize: 13,
          }}
        />
        <button
          onClick={onSend}
          disabled={disabled}
          style={{
            padding: '6px 16px',
            background: theme.brand.primary,
            color: theme.text.onBrand,
            border: 'none',
            borderRadius: theme.radius.md,
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {state.kind === 'sending' ? 'Sending…' : 'Analyse'}
        </button>
      </div>
      {state.kind === 'sent' && (
        <span style={{ color: theme.status.success, fontSize: 12 }}>
          Enqueued at {new Date(state.enqueuedAt).toLocaleString()}. The analysis will appear in the
          Analysis Review queue once the LLM completes.
        </span>
      )}
      {state.kind === 'error' && (
        <span style={{ color: theme.status.error, fontSize: 12 }}>{state.message}</span>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <>
      <dt style={{ color: theme.text.secondary }}>{label}</dt>
      <dd style={{ margin: 0, fontFamily: mono ? theme.fonts.mono : 'inherit' }}>{value}</dd>
    </>
  );
}

function CodeBlock({ data }: { data: unknown }) {
  return <pre style={styles.pre}>{JSON.stringify(data, null, 2)}</pre>;
}

function formatCapturedFrom(captured: AdminScanDetail['capturedFrom']): React.ReactNode {
  if (!captured) return '—';
  const channel = captured.channel ? ` · ${captured.channel}` : '';
  const idSnip = captured.deviceId.slice(0, 8);
  return (
    <span style={{ fontFamily: theme.fonts.mono, fontSize: 13 }} title={captured.deviceId}>
      {captured.platform} {captured.appVersion} (build {captured.buildNumber}){channel} · device{' '}
      {idSnip}…
    </span>
  );
}

function formatTimestamp(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.toLocaleString()} (${value})`;
}

function formatScanDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - minutes * 60);
  return `${minutes}m ${remainingSeconds}s (${ms.toLocaleString()}ms)`;
}

const REVIEW_STATUS_PALETTE: Record<
  ScanAnalysisReviewStatus,
  { bg: string; fg: string; label: string }
> = {
  'pending-review': { bg: theme.status.warning, fg: '#3a2200', label: 'Pending review' },
  'analysis-failed': { bg: theme.status.error, fg: '#3a0000', label: 'Analysis failed' },
  approved: { bg: theme.status.success, fg: '#013220', label: 'Approved' },
  rejected: { bg: theme.surface.borderStrong, fg: theme.text.primary, label: 'Rejected' },
};

function ReviewStatusSection({
  scanId,
  review,
  onReviewUpdated,
}: {
  scanId: string;
  review: ScanAnalysisReview;
  onReviewUpdated: (review: ScanAnalysisReview) => void;
}) {
  const palette = REVIEW_STATUS_PALETTE[review.status];
  // 'pending-review' and 'analysis-failed' are the actionable states that
  // appear in the queue page. For 'approved' / 'rejected' we show the
  // outcome inline (no actionable destination).
  const isActionable = review.status === 'pending-review' || review.status === 'analysis-failed';
  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13 }}>AI Analysis</strong>
        <span
          style={{
            background: palette.bg,
            color: palette.fg,
            padding: '2px 8px',
            borderRadius: theme.radius.sm,
            fontWeight: 700,
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {palette.label}
        </span>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          Analysed {formatTimestamp(review.analysedAt)} · model {review.model}
        </span>
        {isActionable && (
          <Link to="/scan-analysis-queue" style={{ marginLeft: 'auto', fontSize: 13 }}>
            Open in Analysis Review →
          </Link>
        )}
      </div>

      {review.status === 'analysis-failed' && review.analysisError && (
        <div style={{ color: theme.status.error, fontSize: 12, fontFamily: theme.fonts.mono }}>
          {review.analysisError.slice(0, 400)}
          {review.analysisError.length > 400 ? '…' : ''}
        </div>
      )}

      {review.diagnosis && (
        <div style={{ fontSize: 13, color: theme.text.primary, whiteSpace: 'pre-wrap' }}>
          {review.diagnosis}
        </div>
      )}

      {review.proposedCommands.length > 0 && (
        <div style={{ fontSize: 12, color: theme.text.secondary }}>
          {review.proposedCommands.length} proposed command
          {review.proposedCommands.length === 1 ? '' : 's'}
          {review.proposedAppChanges && review.proposedAppChanges.length > 0
            ? ` · ${review.proposedAppChanges.length} proposed app change${review.proposedAppChanges.length === 1 ? '' : 's'}`
            : ''}
        </div>
      )}

      {review.proposedAppChanges && review.proposedAppChanges.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <strong style={{ fontSize: 12, color: theme.text.secondary }}>
            Proposed app-level changes
          </strong>
          {review.proposedAppChanges.map((change, idx) => (
            <div
              key={idx}
              style={{
                padding: '8px 10px',
                background: theme.surface.header,
                border: `1px solid ${theme.surface.border}`,
                borderRadius: theme.radius.sm,
                fontSize: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <code
                  style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: 10,
                    padding: '1px 5px',
                    background: theme.surface.panel,
                    borderRadius: 3,
                  }}
                >
                  {change.category}
                </code>
                <strong>{change.summary}</strong>
                <span
                  style={{
                    fontSize: 11,
                    color:
                      change.priority === 'high'
                        ? theme.status.error
                        : change.priority === 'medium'
                          ? theme.status.warning
                          : theme.text.tertiary,
                  }}
                >
                  ({change.priority})
                </span>
              </div>
              <div style={{ marginTop: 4, color: theme.text.secondary }}>{change.details}</div>
              <ProposedAppChangeResolutionControl
                scanId={scanId}
                index={idx}
                change={change}
                onResolved={(updated) => onReviewUpdated(updated.review)}
              />
            </div>
          ))}
        </div>
      )}

      {(review.status === 'approved' || review.status === 'rejected') && (
        <div style={{ fontSize: 12, color: theme.text.tertiary, display: 'flex', gap: 12 }}>
          <span>
            {review.status === 'approved' ? 'Approved' : 'Rejected'}
            {review.reviewedAt ? ` ${formatTimestamp(review.reviewedAt)}` : ''}
            {review.reviewedBy ? ` by ${review.reviewedBy}` : ''}
          </span>
        </div>
      )}

      {review.reviewerNote && (
        <div
          style={{
            fontSize: 12,
            color: theme.text.primary,
            background: theme.surface.header,
            borderLeft: `3px solid ${theme.surface.borderStrong}`,
            padding: '6px 10px',
            borderRadius: theme.radius.sm,
          }}
        >
          <strong style={{ color: theme.text.tertiary }}>Reviewer note: </strong>
          {review.reviewerNote}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  dl: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: '4px 16px',
    fontSize: 13,
    marginBottom: 24,
  },
  pre: {
    background: theme.surface.panel,
    border: `1px solid ${theme.surface.border}`,
    borderRadius: theme.radius.lg,
    padding: 12,
    fontSize: 12,
    overflow: 'auto',
    maxHeight: 600,
    fontFamily: theme.fonts.mono,
  },
};

const TIER_LABEL: Record<PriorAnalysisSummary['tier'], string> = {
  exact: 'Exact match',
  'year-near': 'Sibling (±2yr)',
  'year-any': 'Sibling',
};

// ──────────────────────────────────────────────────────────────────────────
// LLM Costs section — surfaces per-call breakdown of the LLM spend for
// this scan. Renders BOTH the ingest-side Haiku calls (overall scan
// interpretation + per-DTC explanations, with cache hits visible) AND
// the analyse-scan Sonnet diagnosis agent's accumulated usage.
//
// Defensive against absent data: scans persisted before this feature
// shipped have neither field; failed scans typically have neither;
// scans not yet picked up by analyse-scan have ingest only. When the
// per-call `entries` array is absent the section falls back to a single
// summary row built from the aggregate fields.
// ──────────────────────────────────────────────────────────────────────────

function fmtUsd(usd: number): string {
  if (usd === 0) return '$0';
  if (usd < 0.0001) return '<$0.0001';
  if (usd < 0.01) return `$${usd.toFixed(5)}`;
  if (usd < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(3)}`;
}

function fmtTokens(n: number): string {
  return n.toLocaleString('en-GB');
}

function sourceLabel(source: LlmCallEntry['source']): string {
  switch (source) {
    case 'ingest-overall':
      return 'Overall interpretation';
    case 'ingest-dtc':
      return 'DTC explanation';
    case 'analyse-scan':
      return 'Diagnosis agent';
    case 'dashboard-adhoc':
      return 'Ad-hoc (dashboard)';
  }
}

function LlmCostsSection({ usage }: { usage: NonNullable<AdminScanDetail['llmUsage']> }) {
  const ingest = usage.ingest;
  const analyse = usage.analyseScan;
  if (!ingest && !analyse) return null;

  const totalUsd = (ingest?.totalUsd ?? 0) + (analyse?.totalUsd ?? 0);
  const totalInputTokens = (ingest?.inputTokens ?? 0) + (analyse?.inputTokens ?? 0);
  const totalOutputTokens = (ingest?.outputTokens ?? 0) + (analyse?.outputTokens ?? 0);
  const totalCallCount = (ingest?.callCount ?? 0) + (analyse?.callCount ?? 0);

  // Combine entries from both sources. If a record has no entries array,
  // synthesise ONE row from its aggregate so legacy scans still show
  // something meaningful (otherwise the section would silently be empty).
  const rows: LlmCallEntry[] = [];
  if (ingest) {
    if (ingest.entries && ingest.entries.length > 0) {
      rows.push(...ingest.entries);
    } else {
      rows.push({
        source: 'ingest-overall',
        model: ingest.model,
        startedAt: '',
        latencyMs: ingest.latencyMs,
        inputTokens: ingest.inputTokens,
        outputTokens: ingest.outputTokens,
        costUsd: ingest.totalUsd,
        fromDdbCache: false,
        context: ingest.callCount > 1 ? `${ingest.callCount} calls (legacy aggregate)` : undefined,
      });
    }
  }
  if (analyse) {
    if (analyse.entries && analyse.entries.length > 0) {
      rows.push(...analyse.entries);
    } else {
      rows.push({
        source: 'analyse-scan',
        model: analyse.model,
        startedAt: '',
        latencyMs: analyse.latencyMs,
        inputTokens: analyse.inputTokens,
        outputTokens: analyse.outputTokens,
        cacheReadInputTokens: analyse.cacheReadInputTokens,
        cacheWriteInputTokens: analyse.cacheWriteInputTokens,
        costUsd: analyse.totalUsd,
        fromDdbCache: false,
        context:
          analyse.callCount > 1
            ? `${analyse.callCount} agent cycles (legacy aggregate)`
            : undefined,
      });
    }
  }

  const cacheHits = rows.filter((r) => r.fromDdbCache).length;
  const cachedAggregate = analyse?.cacheReadInputTokens ?? 0;

  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>LLM Costs</h3>
        <span
          style={{
            background: theme.brand.primary,
            color: theme.text.onBrand,
            padding: '2px 8px',
            borderRadius: theme.radius.sm,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.4,
            fontFamily: theme.fonts.mono,
          }}
        >
          {fmtUsd(totalUsd)}
        </span>
        <span style={{ color: theme.text.secondary, fontSize: 12 }}>
          {totalCallCount} {totalCallCount === 1 ? 'call' : 'calls'}
          {cacheHits > 0 ? ` · ${cacheHits} cached` : ''}
          {' · '}
          {fmtTokens(totalInputTokens)} in / {fmtTokens(totalOutputTokens)} out
          {cachedAggregate > 0 ? ` (+${fmtTokens(cachedAggregate)} prompt-cache)` : ''}
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
            fontFamily: theme.fonts.mono,
          }}
        >
          <thead>
            <tr style={{ textAlign: 'left', color: theme.text.secondary }}>
              <th style={{ padding: '4px 8px 4px 0' }}>Source</th>
              <th style={{ padding: '4px 8px' }}>Context</th>
              <th style={{ padding: '4px 8px' }}>Model</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>In</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Out</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Cost</th>
              <th style={{ padding: '4px 8px', textAlign: 'right' }}>Latency</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                style={{
                  borderTop: `1px solid ${theme.surface.border}`,
                  color: row.fromDdbCache ? theme.text.tertiary : theme.text.primary,
                  fontStyle: row.fromDdbCache ? 'italic' : 'normal',
                }}
              >
                <td style={{ padding: '4px 8px 4px 0' }}>
                  {sourceLabel(row.source)}
                  {row.fromDdbCache && (
                    <span
                      style={{
                        marginLeft: 6,
                        color: theme.text.tertiary,
                        fontSize: 10,
                        padding: '1px 4px',
                        border: `1px solid ${theme.surface.borderStrong}`,
                        borderRadius: theme.radius.sm,
                      }}
                    >
                      CACHED
                    </span>
                  )}
                </td>
                <td style={{ padding: '4px 8px' }}>{row.context ?? '—'}</td>
                <td style={{ padding: '4px 8px' }}>{row.model}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                  {fmtTokens(row.inputTokens)}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                  {fmtTokens(row.outputTokens)}
                </td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtUsd(row.costUsd)}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                  {row.latencyMs > 0 ? `${row.latencyMs} ms` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: theme.text.tertiary }}>
        Cost computed from Bedrock per-token rates published at the time of the call. DDB-cached DTC
        explanations show $0 (no Bedrock invocation).
        {!ingest?.entries && ingest
          ? ' Ingest aggregate shown — per-call breakdown will appear on scans persisted after this feature shipped.'
          : ''}
      </div>
    </div>
  );
}

function PriorAnalysesSection({ analyses }: { analyses: PriorAnalysisSummary[] }) {
  const exactCount = analyses.filter((a) => a.tier === 'exact').length;
  const siblingCount = analyses.length - exactCount;
  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        background: theme.surface.panel,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Prior AI analyses for this vehicle</strong>
        <span style={{ color: theme.text.tertiary, fontSize: 12 }}>
          {exactCount} exact match{exactCount === 1 ? '' : 'es'}
          {siblingCount > 0 ? ` · ${siblingCount} sibling${siblingCount === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {analyses.map((a) => (
          <PriorAnalysisRow key={a.scanId} analysis={a} />
        ))}
      </div>
    </div>
  );
}

function PriorAnalysisRow({ analysis }: { analysis: PriorAnalysisSummary }) {
  const palette = REVIEW_STATUS_PALETTE[analysis.status];
  return (
    <div
      style={{
        padding: '10px 12px',
        background: theme.surface.header,
        border: `1px solid ${theme.surface.border}`,
        borderRadius: theme.radius.md,
        fontSize: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            background: palette.bg,
            color: palette.fg,
            padding: '1px 6px',
            borderRadius: theme.radius.sm,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
        >
          {palette.label}
        </span>
        <span
          style={{
            color: theme.text.tertiary,
            fontSize: 11,
            padding: '1px 6px',
            border: `1px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.sm,
          }}
        >
          {TIER_LABEL[analysis.tier]}
        </span>
        <Link
          to={`/scans/${encodeURIComponent(analysis.scanId)}`}
          style={{ fontSize: 12, fontFamily: theme.fonts.mono }}
        >
          {analysis.scanId.slice(0, 8)}…
        </Link>
        <span style={{ color: theme.text.tertiary, marginLeft: 'auto' }}>
          {formatTimestamp(analysis.analysedAt)} · model {analysis.model}
        </span>
      </div>
      {analysis.diagnosisExcerpt && (
        <div style={{ marginTop: 6, color: theme.text.primary }}>{analysis.diagnosisExcerpt}</div>
      )}
      {analysis.proposedAppChangeSummaries.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: theme.text.secondary }}>
          {analysis.proposedAppChangeSummaries.map((s, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <code
                  style={{
                    fontFamily: theme.fonts.mono,
                    fontSize: 10,
                    padding: '1px 5px',
                    background: theme.surface.panel,
                    borderRadius: 3,
                  }}
                >
                  {s.category}
                </code>
                <span>{s.summary}</span>
                {s.resolution && (
                  <ResolutionBadge resolution={s.resolution} note={s.resolutionNote} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {analysis.reviewerNote && (
        <div
          style={{
            marginTop: 6,
            padding: '4px 8px',
            background: theme.surface.panel,
            borderLeft: `3px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.sm,
          }}
        >
          <strong style={{ color: theme.text.tertiary }}>Reviewer note: </strong>
          {analysis.reviewerNote}
          {analysis.reviewedByDisplayName ? ` — ${analysis.reviewedByDisplayName}` : ''}
        </div>
      )}
    </div>
  );
}

// Staff-side inline editor for the registration plate. Three states:
//   - view:    show "AB12 CDE" (or "—") + an "Edit" button
//   - editing: show a text input + Save / Cancel / Clear buttons
//   - saving:  buttons disabled, "Saving…" label
// Validation runs live with the same shared validator the mobile app uses,
// so the UX feedback is identical. Errors from the server (network /
// race conditions) surface inline below the input.
function RegistrationPlateEditor({
  scanId,
  current,
  onSaved,
}: {
  scanId: string;
  current: string | null;
  onSaved: (plate: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current ?? '');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    // Reset the editor's local input whenever the parent's stored plate
    // changes (e.g. a separate tab edited it via the same API).
    if (!editing) setValue(current ?? '');
  }, [current, editing]);

  if (!editing) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: theme.fonts.mono }}>{current ?? '—'}</span>
        <button
          type="button"
          onClick={() => {
            setValue(current ?? '');
            setEditing(true);
            setServerError(null);
          }}
          style={{
            padding: '2px 10px',
            fontSize: 12,
            background: theme.surface.panel,
            border: `1px solid ${theme.surface.borderStrong}`,
            color: theme.text.primary,
            borderRadius: theme.radius.sm,
            cursor: 'pointer',
          }}
        >
          {current ? 'Edit' : 'Add'}
        </button>
      </span>
    );
  }

  const isEmpty = value.trim().length === 0;
  const validation = validateRegistrationPlate(value);
  const canSave = !saving && (isEmpty || validation.valid);

  const save = async () => {
    setSaving(true);
    setServerError(null);
    try {
      const toSend = isEmpty ? null : validation.valid ? validation.normalised : null;
      await updateScan(scanId, { registrationPlate: toSend });
      onSaved(toSend);
      setEditing(false);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValue(current ?? '');
    setServerError(null);
    setEditing(false);
  };

  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          value={value}
          onChange={(e) => {
            setValue(e.target.value.toUpperCase());
            if (serverError) setServerError(null);
          }}
          placeholder="AB12 CDE"
          style={{
            fontFamily: theme.fonts.mono,
            padding: '4px 8px',
            border: `1px solid ${
              !isEmpty && !validation.valid ? theme.status.error : theme.surface.borderStrong
            }`,
            background: theme.surface.panel,
            color: theme.text.primary,
            borderRadius: theme.radius.sm,
            width: 140,
          }}
          maxLength={12}
          autoFocus
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            background: canSave ? theme.brand.primary : theme.surface.panel,
            border: 'none',
            color: canSave ? theme.text.onBrand : theme.text.tertiary,
            borderRadius: theme.radius.sm,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >
          {saving ? 'Saving…' : isEmpty ? 'Clear' : 'Save'}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          style={{
            padding: '4px 12px',
            fontSize: 12,
            background: 'transparent',
            border: 'none',
            color: theme.text.tertiary,
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </span>
      {!isEmpty && !validation.valid && (
        <span style={{ fontSize: 11, color: theme.status.error }}>{validation.error}</span>
      )}
      {serverError && (
        <span style={{ fontSize: 11, color: theme.status.error }}>{serverError}</span>
      )}
    </span>
  );
}

// MarkAsFailedControl
//
// Three rendering states:
//   1. marked-failed -> red banner with reason + audit + "Unmark" button
//   2. confirming    -> reason input + Save/Cancel (after Mark button click)
//   3. idle          -> "Mark scan as failed" button only
//
// Server-side gating (admin-api PATCH, analyse-scan skip, regenerate-pdf 409)
// is the source of truth; this UI is the operator's only way to invoke it.
// The component is intentionally chunkier than RegistrationPlateEditor: marking
// a scan as failed has bigger downstream consequences (LLM skipped, PDF refused,
// future analytics exclusion) so the confirmation step is more deliberate.
function MarkAsFailedControl({
  scan,
  onUpdated,
}: {
  scan: AdminScanDetail;
  onUpdated: (
    patch: Partial<
      Pick<
        AdminScanDetail,
        | 'operatorOutcome'
        | 'operatorOutcomeReason'
        | 'operatorOutcomeSetBy'
        | 'operatorOutcomeSetAt'
      >
    >,
  ) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const isMarkedFailed = scan.operatorOutcome === 'failed';

  // State 1: already marked failed — show banner + unmark button.
  if (isMarkedFailed) {
    const setBy = scan.operatorOutcomeSetBy ?? 'unknown';
    const setAt = scan.operatorOutcomeSetAt
      ? new Date(scan.operatorOutcomeSetAt).toLocaleString('en-GB')
      : 'unknown';
    const unmark = async () => {
      setSaving(true);
      setServerError(null);
      try {
        await updateScan(scan.scanId, { operatorOutcome: null });
        onUpdated({
          operatorOutcome: null,
          operatorOutcomeReason: null,
          operatorOutcomeSetBy: null,
          operatorOutcomeSetAt: null,
        });
      } catch (err) {
        setServerError(err instanceof Error ? err.message : 'Unmark failed');
      } finally {
        setSaving(false);
      }
    };
    return (
      <div
        style={{
          marginTop: 12,
          padding: '12px 14px',
          border: `1px solid ${theme.status.error}`,
          borderRadius: theme.radius.md,
          background: theme.surface.panel,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              background: theme.status.error,
              color: theme.text.onBrand,
              padding: '2px 10px',
              borderRadius: theme.radius.sm,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.4,
            }}
          >
            MARKED FAILED
          </span>
          <span style={{ fontSize: 12, color: theme.text.secondary }}>
            by {setBy} · {setAt}
          </span>
          <button
            type="button"
            onClick={() => void unmark()}
            disabled={saving}
            style={{
              marginLeft: 'auto',
              padding: '4px 12px',
              fontSize: 12,
              background: theme.surface.panel,
              border: `1px solid ${theme.surface.borderStrong}`,
              color: theme.text.primary,
              borderRadius: theme.radius.sm,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Unmarking…' : 'Unmark'}
          </button>
        </div>
        {scan.operatorOutcomeReason && (
          <div style={{ fontSize: 13, color: theme.text.primary, fontStyle: 'italic' }}>
            Reason: {scan.operatorOutcomeReason}
          </div>
        )}
        <div style={{ fontSize: 11, color: theme.text.tertiary }}>
          Downstream consumers (AI analysis, PDF regeneration) refuse to run on marked-failed scans.
          Unmark above to restore normal processing.
        </div>
        {serverError && (
          <div style={{ fontSize: 11, color: theme.status.error }}>{serverError}</div>
        )}
      </div>
    );
  }

  // State 2: confirming — show reason input + save/cancel.
  if (confirming) {
    const trimmedReason = reason.trim();
    const tooLong = trimmedReason.length > 1_000;
    const canSave = !saving && !tooLong;
    const save = async () => {
      setSaving(true);
      setServerError(null);
      try {
        await updateScan(scan.scanId, {
          operatorOutcome: 'failed',
          operatorOutcomeReason: trimmedReason.length > 0 ? trimmedReason : null,
        });
        // Server stamps SetBy/SetAt; the next page refresh will surface them.
        // We optimistically update operatorOutcome + reason; setBy/setAt left
        // null until the next getScan() call (typically on page reload).
        onUpdated({
          operatorOutcome: 'failed',
          operatorOutcomeReason: trimmedReason.length > 0 ? trimmedReason : null,
        });
        setConfirming(false);
        setReason('');
      } catch (err) {
        setServerError(err instanceof Error ? err.message : 'Mark failed');
      } finally {
        setSaving(false);
      }
    };
    return (
      <div
        style={{
          marginTop: 12,
          padding: '12px 14px',
          border: `1px solid ${theme.surface.borderStrong}`,
          borderRadius: theme.radius.md,
          background: theme.surface.panel,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, color: theme.text.primary }}>
          Mark this scan as failed? It will be excluded from AI analysis and PDF regeneration. Add
          an optional reason so future reviewers know why.
        </div>
        <textarea
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (serverError) setServerError(null);
          }}
          placeholder="Why is this scan unusable? (optional, max 1000 chars)"
          maxLength={1_100}
          rows={3}
          style={{
            fontFamily: theme.fonts.sans,
            fontSize: 13,
            padding: '6px 8px',
            border: `1px solid ${tooLong ? theme.status.error : theme.surface.borderStrong}`,
            background: theme.surface.page,
            color: theme.text.primary,
            borderRadius: theme.radius.sm,
            resize: 'vertical',
          }}
          autoFocus
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => void save()}
            disabled={!canSave}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              background: canSave ? theme.status.error : theme.surface.panel,
              border: 'none',
              color: canSave ? theme.text.onBrand : theme.text.tertiary,
              borderRadius: theme.radius.sm,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Marking…' : 'Mark as failed'}
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              setReason('');
              setServerError(null);
            }}
            disabled={saving}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              background: theme.surface.panel,
              border: `1px solid ${theme.surface.borderStrong}`,
              color: theme.text.primary,
              borderRadius: theme.radius.sm,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          {tooLong && (
            <span style={{ fontSize: 11, color: theme.status.error }}>
              Reason exceeds 1000 characters.
            </span>
          )}
        </div>
        {serverError && (
          <div style={{ fontSize: 11, color: theme.status.error }}>{serverError}</div>
        )}
      </div>
    );
  }

  // State 3: idle — show only the trigger button.
  return (
    <div style={{ marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        style={{
          padding: '6px 14px',
          fontSize: 12,
          background: theme.surface.panel,
          border: `1px solid ${theme.surface.borderStrong}`,
          color: theme.text.secondary,
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
        }}
      >
        Mark this scan as failed…
      </button>
    </div>
  );
}
